/**
 * @fileoverview Help & Support Page
 * @theme Polished Corporate - Teal accents, refined shadows
 */

import { useState } from 'react';
import { Button } from '@components/ui/button';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'How do I create a new invoice?',
    answer: 'Navigate to Accounts Receivable > Invoices and click the "New Invoice" button. Fill in the customer details, add line items, and click "Create Invoice" to generate your invoice.',
  },
  {
    question: 'How do I record a payment for a bill?',
    answer: 'Go to Accounts Payable > Bills, find the bill you want to pay, and click the "Record Payment" button. Enter the payment details including date, amount, and payment method.',
  },
  {
    question: 'Can I import data from other accounting software?',
    answer: 'Yes! Go to Settings > Integrations to connect with QuickBooks, Xero, or other accounting software. You can also import CSV files with your existing data.',
  },
  {
    question: 'How does the AI automation work?',
    answer: 'Our AI automatically categorizes transactions, matches invoices with payments, and suggests GL accounts based on your historical patterns. The more you use the system, the smarter it gets.',
  },
  {
    question: 'What security measures protect my data?',
    answer: 'We use bank-level encryption (AES-256), two-factor authentication, and SOC 2 Type II certified infrastructure. Your data is backed up daily and stored in secure, redundant data centers.',
  },
];

export function HelpPage(): JSX.Element {
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
  });

  const toggleFAQ = (index: number): void => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gray-50/30">
      <div className="container py-6">
        {/* Hero Section */}
        <div className="mb-8 rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 p-8 text-white shadow-lg">
          <h2 className="text-2xl font-bold">How can we help you?</h2>
          <p className="mt-2 text-teal-100">
            Find answers to common questions or reach out to our support team
          </p>
          <div className="mt-4 flex max-w-xl">
            <input
              type="text"
              placeholder="Search for help..."
              className="flex-1 rounded-l-xl border-0 bg-white/20 px-4 py-3 text-white placeholder-teal-200 backdrop-blur focus:outline-none focus:ring-2 focus:ring-white/50"
            />
            <button className="rounded-r-xl bg-white px-6 font-medium text-teal-600 hover:bg-teal-50 transition-colors">
              Search
            </button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Quick Links */}
          <div className="lg:col-span-1">
            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="font-semibold text-gray-900">Quick Links</h3>
              <div className="mt-4 space-y-3">
                {[
                  { title: 'Getting Started Guide', icon: '📚' },
                  { title: 'Video Tutorials', icon: '🎬' },
                  { title: 'API Documentation', icon: '🔧' },
                  { title: 'Release Notes', icon: '📋' },
                  { title: 'Community Forum', icon: '💬' },
                ].map((link) => (
                  <button
                    key={link.title}
                    className="flex w-full items-center gap-3 rounded-xl border border-gray-100 p-3 text-left transition-all hover:border-teal-200 hover:bg-teal-50"
                  >
                    <span className="text-lg">{link.icon}</span>
                    <span className="text-sm font-medium text-gray-700">{link.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Support */}
            <div className="mt-6 rounded-xl bg-white p-6 shadow-card">
              <h3 className="font-semibold text-gray-900">Contact Support</h3>
              <p className="mt-1 text-sm text-gray-500">Can't find what you're looking for?</p>

              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subject</label>
                  <input
                    type="text"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    placeholder="Brief description of your issue"
                    className="mt-1.5 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Message</label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="Describe your issue in detail..."
                    rows={4}
                    className="mt-1.5 w-full resize-none rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  />
                </div>
                <Button className="w-full rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 text-white">
                  Send Message
                </Button>
              </div>

              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Average response time: 2 hours</span>
              </div>
            </div>
          </div>

          {/* FAQs */}
          <div className="lg:col-span-2">
            <div className="rounded-xl bg-white p-6 shadow-card">
              <h3 className="font-semibold text-gray-900">Frequently Asked Questions</h3>
              <p className="mt-1 text-sm text-gray-500">Quick answers to common questions</p>

              <div className="mt-6 space-y-3">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="rounded-xl border border-gray-100 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-gray-50"
                    >
                      <span className="font-medium text-gray-900">{faq.question}</span>
                      <svg
                        className={`h-5 w-5 text-gray-400 transition-transform ${
                          expandedFAQ === index ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedFAQ === index && (
                      <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3">
                        <p className="text-sm text-gray-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Cards */}
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl bg-white p-6 shadow-card">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h4 className="mt-4 font-semibold text-gray-900">Email Support</h4>
                <p className="mt-1 text-sm text-gray-500">support@finova.com</p>
                <p className="text-xs text-gray-400">Response within 24 hours</p>
              </div>
              <div className="rounded-xl bg-white p-6 shadow-card">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <h4 className="mt-4 font-semibold text-gray-900">Phone Support</h4>
                <p className="mt-1 text-sm text-gray-500">+1 (800) 555-0123</p>
                <p className="text-xs text-gray-400">Mon-Fri 9am-6pm EST</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
